import { useMemo, useState } from 'react';
import type { PersonOption } from '../types';

/**
 * Type-ahead selection of PEOPLE (never a roster Contact). The list can hold every person in a district, so it is a
 * search box, not a <select>. Matches name or PER reference; shows the reference so two people with the same name
 * are told apart, and marks unconfirmed (provisional) people.
 *
 * "Not listed?" never creates a bare record: `onAddPerson` opens the normal Add Person flow, which runs the
 * duplicate check first.
 */
interface CommonProps {
  /** One option per (person, district); the picker narrows to `districtId`. */
  options: PersonOption[];
  districtId: string;
  /** Person ids that must not be offered again (already chosen, or the owner). */
  exclude?: string[];
  /** Further narrowing, e.g. commissioner-eligible people only. */
  filter?: (o: PersonOption) => boolean;
  /** Name for an id that is not in `options` (a person outside the viewer's scope). */
  labelFor: (personId: string) => string | null;
  placeholder?: string;
  emptyHint?: string;
  onAddPerson?: () => void;
  disabled?: boolean;
}

const norm = (v: string) => v.toLowerCase().normalize('NFKD').replace(/[^\p{L}\p{N}\s-]/gu, '');

function useMatches({ options, districtId, exclude = [], filter }: CommonProps, query: string): PersonOption[] {
  return useMemo(() => {
    const q = norm(query.trim());
    if (!q) return [];
    const seen = new Set<string>();
    const out: PersonOption[] = [];
    for (const o of options) {
      if (o.districtId !== districtId || seen.has(o.id) || exclude.includes(o.id)) continue;
      if (filter && !filter(o)) continue;
      if (norm(o.name).includes(q) || (o.ref ?? '').toLowerCase().includes(q)) { seen.add(o.id); out.push(o); }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name)).slice(0, 8);
  }, [options, districtId, exclude, filter, query]);
}

function Option({ o, onPick }: { o: PersonOption; onPick: () => void }) {
  return (
    <button type="button" className="people-option" onClick={onPick}>
      {o.name}
      {o.ref ? <span className="subtitle"> · {o.ref}</span> : null}
      {o.provisional ? <span className="subtitle"> · unconfirmed</span> : null}
    </button>
  );
}

function Results({ props, query, matches, onPick }: { props: CommonProps; query: string; matches: PersonOption[]; onPick: (o: PersonOption) => void }) {
  if (!query.trim()) return null;
  return (
    <div className="people-dropdown">
      {matches.map((o) => <Option key={o.id} o={o} onPick={() => onPick(o)} />)}
      {matches.length === 0 && (
        <div className="people-option-empty">
          {props.emptyHint ?? 'No one matches. Check the spelling, or add them as a new person.'}
          {props.onAddPerson && (
            <div><button type="button" className="btn" style={{ marginTop: 6 }} onClick={props.onAddPerson}>Add a person…</button></div>
          )}
        </div>
      )}
    </div>
  );
}

export function PersonPicker(props: CommonProps & { value: string; onChange: (personId: string) => void; allowClear?: boolean }) {
  const [query, setQuery] = useState('');
  const matches = useMatches(props, query);
  const selected = props.value ? props.labelFor(props.value) ?? 'Unknown person' : null;
  if (props.value && selected !== null) {
    return (
      <div className="people-chips">
        <span className="people-chip">
          {selected}
          {!props.disabled && (
            <button type="button" className="people-chip-remove" aria-label={`Change ${selected}`} onClick={() => props.onChange('')}>×</button>
          )}
        </span>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <input
        placeholder={props.placeholder ?? 'Search by name…'}
        value={query}
        disabled={props.disabled}
        onChange={(e) => setQuery(e.target.value)}
      />
      <Results props={props} query={query} matches={matches} onPick={(o) => { props.onChange(o.id); setQuery(''); }} />
    </div>
  );
}

export function PersonMultiPicker(props: CommonProps & { values: string[]; onChange: (personIds: string[]) => void; max?: number }) {
  const [query, setQuery] = useState('');
  const exclude = useMemo(() => [...(props.exclude ?? []), ...props.values], [props.exclude, props.values]);
  const matches = useMatches({ ...props, exclude }, query);
  const full = props.max !== undefined && props.values.length >= props.max;
  return (
    <>
      {props.values.length > 0 && (
        <div className="people-chips">
          {props.values.map((id) => {
            const name = props.labelFor(id) ?? 'Unknown person';
            return (
              <span key={id} className="people-chip">
                {name}
                {!props.disabled && (
                  <button type="button" className="people-chip-remove" aria-label={`Remove ${name}`} onClick={() => props.onChange(props.values.filter((x) => x !== id))}>×</button>
                )}
              </span>
            );
          })}
        </div>
      )}
      {!full && (
        <div style={{ position: 'relative' }}>
          <input placeholder={props.placeholder ?? 'Search people to add…'} value={query} disabled={props.disabled} onChange={(e) => setQuery(e.target.value)} />
          <Results props={props} query={query} matches={matches} onPick={(o) => { props.onChange([...props.values, o.id]); setQuery(''); }} />
        </div>
      )}
      {full && <div className="subtitle">Up to {props.max} people.</div>}
    </>
  );
}
